import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { site, type PageMeta } from "@/content/site";

interface WebflowRuntime {
  destroy(): void;
  ready(): void;
  require(name: string): { init?(): void } | undefined;
}

declare global {
  interface Window {
    Webflow?: WebflowRuntime;
  }
}

/**
 * Re-runs the original Webflow runtime (public/webflow/webflow.<hash>.js) against the
 * freshly rendered page — this is what drives the IX2 scroll/load interactions,
 * Lottie animations and the mobile nav, exactly as on the Webflow-hosted site.
 * Call after the page DOM has mounted.
 */
export function useWebflowPage(meta: PageMeta, key: string) {
  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute("data-wf-site", site.wfSiteId);
    html.setAttribute("data-wf-page", meta.wfPageId);
    document.title = meta.title;
    document.querySelector('meta[name="description"]')?.setAttribute("content", meta.description);
    if (!window.location.hash) window.scrollTo(0, 0);

    const wf = window.Webflow;
    if (!wf) return;
    wf.destroy();
    wf.ready();
    wf.require("ix2")?.init?.();
    // IX2's "page start/finish" triggers listen for readystatechange, which has
    // long fired by the time React renders — replay it so load animations run.
    document.dispatchEvent(new Event("readystatechange"));
  }, [meta, key]);
}

/**
 * The ported markup uses plain <a href> (to keep it identical to Webflow's).
 * Intercept same-origin clicks and route them client-side instead of reloading.
 */
export function useClientSideLinks() {
  const navigate = useNavigate();
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.("a");
      if (!a || !a.href || a.hasAttribute("download")) return;
      if (a.target && a.target !== "_self") return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.hash) return; // in-page anchor
      e.preventDefault();
      navigate(url.pathname + url.search + url.hash);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [navigate]);
}

/** Returns a helper that appends Webflow's `w--current` class to links pointing at the current page. */
export function useCurrent() {
  const { pathname } = useLocation();
  return useCallback(
    (href: string, className: string) => (href === pathname ? `${className} w--current` : className),
    [pathname],
  );
}
